import { canonicalUrl } from "@/config/canonical";
import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import Card from "@/design-system/Card/Card";
import { SectionLabel } from "@/design-system/SectionLabel/SectionLabel";
import { Skeleton } from "@/design-system/Skeleton/Skeleton";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { useStartClubImport } from "@/features/MyClub/EmptyClub/useStartClubImport";
import { WingsCupBanner } from "@/features/WingsCup/WingsCup";
import { useGridColumnCount } from "@/hooks/useGridColumnCount";
import { globalStateSelector } from "@/redux/globalState/globalStateSlice";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  getUserSelector,
  userIsLoggedInSelector,
  userProfileResolvedSelector,
} from "@/redux/user/userSlice";
import {
  GALLERY_STORAGE_KEYS,
  type GallerySort,
  type GalleryStatus,
  grouped,
  isGallerySort,
} from "@repo/shared/gallery";
import { useGalleryList, useGalleryListPage } from "@repo/shared/hooks";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { useCreditGate } from "@repo/shared/redux";
import { storage } from "@repo/shared/storage";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router";

import { GalleryPlanner } from "./components/GalleryPlanner";
import {
  GallerySetCard,
  GallerySetCardSkeleton,
  GalleryTokenIcon,
} from "./components/GallerySetCard";
import { GallerySteps } from "./components/GallerySteps";
import {
  GalleryCategoryControl,
  GallerySortControl,
  GalleryToolbar,
} from "./components/GalleryToolbar";
import {
  galleryStepsSync,
  useCollectedPlayersSync,
} from "./components/useCollectedPlayersSync";

const CLUB_IMPORT_ORIGIN = { section: "Gallery", subsection: "List" };

export const GalleryPage = () => {
  const { t, i18n } = useTranslation("gallery");
  const [searchTerm, setSearchTerm] = useState("");
  const seo = useSeoCopy("gallery");
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector(getUserSelector);
  const list = useGalleryList({ userUuid: user.uuid, platform: user.platform });
  const { categories, progress, gradesBySetId, refresh } = list;
  // A sync completed in this visit: the solutions built before it are stale.
  const [syncedThisVisit, setSyncedThisVisit] = useState(false);
  const sync = useCollectedPlayersSync(() => {
    setSyncedThisVisit(true);
    refresh();
  });
  const dispatch = useDispatch<AppDispatch>();
  const creditGate = useCreditGate("gallery-solutions-built");
  const { lastImportFinishedAt } = useSelector(globalStateSelector);
  const startClubImport = useStartClubImport(CLUB_IMPORT_ORIGIN);
  const location = useLocation();
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const profileResolved = useSelector(userProfileResolvedSelector);
  const [gridRef, columns] = useGridColumnCount();
  // One skeleton until the page can show its final state: the sets, plus a logged-in user's profile and progress.
  const waiting =
    list.status === "pending" ||
    (isLoggedIn && !profileResolved) ||
    list.progressLoading;
  // Like the SBC set page: playerCount, since the profile loads before any club fetch.
  const clubImported = !!user.uuid && user.playerCount > 0;

  const statusParam = searchParams.get("status");
  const statusFilter: GalleryStatus =
    // Available by default, so the URL only names the other two.
    statusParam === "all" || statusParam === "completed"
      ? statusParam
      : "available";
  // The URL's sort (a shared link), else the cached one.
  const sortParam =
    searchParams.get("sort") ?? storage.get(GALLERY_STORAGE_KEYS.sort);
  const page = useGalleryListPage({
    list,
    categorySlug: searchParams.get("category"),
    status: statusFilter,
    chosenSort: isGallerySort(sortParam) ? sortParam : "newest",
    searchTerm,
    allLabel: t("list.all"),
    clubImported,
    syncRunning: sync.running,
  });
  // The Gallery Planner's plan: its sets first, each on its planned grade, the rest below a divider.
  const { planner, category, allCategory } = page;
  const { plan, plannedGrades } = planner;
  const setParam = useCallback(
    (key: string, value?: string) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const chooseSort = useCallback(
    (s: GallerySort) => {
      storage.set(GALLERY_STORAGE_KEYS.sort, s);
      setParam("sort", s === "newest" ? "" : s);
    },
    [setParam],
  );

  useEffect(() => {
    window.hj?.("event", "landed-on-gallery");
  }, []);

  // A club import's Done, or coming back to the tab (a pack opened in the Web App may have collected players): read the steps again.
  const importFinishedAtMount = useRef(lastImportFinishedAt);
  useEffect(() => {
    if (lastImportFinishedAt !== importFinishedAtMount.current) refresh();
  }, [lastImportFinishedAt, refresh]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const { startSolves: queueSolves } = list;
  const startSolves = useCallback(
    (scope: { categoryId?: number }) => {
      if (!user.uuid) {
        dispatch(showModal({ modalType: "login-required" }));
        return;
      }
      if (!creditGate()) return;
      // A build switches the list to Recommended, which ranks the fresh solutions.
      chooseSort("recommended");
      void queueSolves(scope);
    },
    [user.uuid, dispatch, creditGate, queueSolves, chooseSort],
  );

  const stepsSync = galleryStepsSync(
    sync,
    list.collectedSync,
    syncedThisVisit,
    i18n.language,
  );

  return (
    <main className="z-10 h-full min-h-screen flex-1 bg-background text-white">
      <HeroSection
        helmetTitle={seo.title}
        helmetDescription={seo.description}
        pageTitle={seo.pageTitle}
        subtitle={seo.subtitle}
        helmetCanonicalLink={canonicalUrl("/fut-gallery")}
        shouldBeInTheDocumentFlow
      />
      {waiting && (
        <div className="mx-auto flex w-full max-w-content flex-col gap-y-2 px-4 pt-4 large-desktop:px-8">
          <Card hoverable={false} className="flex flex-col gap-y-4 px-4 py-3">
            <Skeleton className="w-24 text-[10px]" />
            <div className="flex gap-x-4">
              <Skeleton className="h-8 w-36 rounded-full" />
              <Skeleton className="h-8 w-44 rounded-full" />
            </div>
            <Skeleton className="w-52 text-[10px]" />
            <Skeleton className="w-32 text-[10px]" />
          </Card>
        </div>
      )}
      {!waiting && categories.length > 0 && (
        <div className="mx-auto flex w-full max-w-content flex-col gap-y-2 px-4 pt-4 large-desktop:px-8">
          <GallerySteps
            clubImported={clubImported}
            lastImportedAt={user.lastImportedAt}
            onImportClub={() =>
              void startClubImport({
                returnTo: location.pathname + location.search,
              })
            }
            build={{
              ...page.build,
              costsCredit: !!user.uuid,
              onBuild: () => startSolves(page.solveScope),
            }}
            sync={stepsSync}
          >
            <GalleryPlanner
              key={planner.fieldsKey}
              plan={plan}
              done={page.planDone}
              categoryId={category.id || undefined}
              // The most reachable: every open grade's tokens.
              tokensPlaceholder={grouped.format(page.maxOpenTokens)}
              budgetPlaceholder={grouped.format(list.limits.coins ?? 0)}
              setsPlaceholder={grouped.format(category.sets.length)}
              building={planner.building}
              empty={planner.empty}
              error={planner.error ? t("plan.error") : undefined}
              onBuild={(input) => void planner.build(input)}
              onClear={planner.clear}
              open={page.plannerOpen}
              highlight={page.plannerHighlight}
              onToggle={page.togglePlanner}
            />
          </GallerySteps>
        </div>
      )}
      <div className="mt-4">
        <GalleryToolbar
          searchTerm={searchTerm}
          onSearch={setSearchTerm}
          status={statusFilter}
          onStatusChanged={(s) =>
            setParam("status", s === "available" ? "" : s)
          }
          statusCounts={page.statusCounts}
          categoryControl={
            categories.length > 0 && (
              <GalleryCategoryControl
                categories={[allCategory, ...categories]}
                category={category}
                onCategoryChanged={(c) => setParam("category", c.slug)}
              />
            )
          }
          sortControl={
            categories.length > 0 && (
              <GallerySortControl
                sort={page.sort}
                onSortChanged={chooseSort}
                sortLockedHint={
                  plannedGrades ? t("plan.sortLocked") : undefined
                }
              />
            )
          }
        />
      </div>
      {list.status === "rejected" ? (
        <p className="mt-4 text-center text-sm text-foreground-muted">
          {t("list.error")}
        </p>
      ) : waiting ? (
        <section className="mx-auto grid w-full max-w-content grid-cols-1 gap-4 px-4 pb-12 pt-4 mobile:grid-cols-2 touch-device:grid-cols-3 large-desktop:grid-cols-4 large-desktop:px-8">
          {Array.from({ length: 8 }, (_, i) => (
            <GallerySetCardSkeleton key={i} />
          ))}
        </section>
      ) : !categories.length ? (
        <p className="mt-4 text-center text-sm text-foreground-muted">
          {t("list.empty")}
        </p>
      ) : !page.visible.length && !page.notInPlan.length ? (
        <p className="mt-4 text-center text-sm text-foreground-muted">
          {plan && !searchTerm ? t("plan.noneInCategory") : t("list.noMatches")}
        </p>
      ) : (
        <section
          ref={gridRef}
          className="mx-auto grid w-full max-w-content grid-cols-1 gap-4 px-4 pb-12 pt-4 mobile:grid-cols-2 touch-device:grid-cols-3 large-desktop:grid-cols-4 large-desktop:px-8"
        >
          {plan && !searchTerm && (
            <div className="col-span-full flex flex-col gap-1">
              <div className="flex items-center justify-between gap-4">
                <h2 className="font-oswald text-2xl font-semibold uppercase leading-tight tracking-wide">
                  <Trans
                    ns="gallery"
                    i18nKey="plan.listTitle"
                    values={{ tokens: grouped.format(plan.totals.tokens) }}
                    components={{
                      icon: (
                        <GalleryTokenIcon className="mr-1.5 inline-block size-6 align-[-0.1em]" />
                      ),
                    }}
                  />
                </h2>
                <TextButton
                  size="medium"
                  onClick={planner.clear}
                  className="hidden mobile:flex"
                >
                  {t("plan.clear")}
                </TextButton>
              </div>
              {!page.visible.length && (
                <p className="text-sm text-foreground-muted">
                  {t("plan.noneInCategory")}
                </p>
              )}
            </div>
          )}
          {[...page.visible, ...page.notInPlan].map((set, index) => {
            const plannedGrade = plannedGrades?.get(set.id);
            return (
              // A new sort or plan resets manual grade picks to the sort's or plan's pick.
              <Fragment key={`${set.id}-${page.sort}-${plannedGrade ?? ""}`}>
                {index === page.visible.length && page.notInPlan.length > 0 && (
                  <div className="col-span-full flex items-center gap-3 pt-4">
                    <span className="h-px flex-1 bg-neutral-700" />
                    <SectionLabel>{t("plan.notInPlan")}</SectionLabel>
                    <span className="h-px flex-1 bg-neutral-700" />
                  </div>
                )}
                <GallerySetCard
                  set={set}
                  categorySlug={page.categorySlugBySetId.get(set.id) ?? ""}
                  progress={progress.get(set.id)}
                  cardGrades={gradesBySetId.get(set.id) ?? []}
                  sortPick={
                    plannedGrade
                      ? set.grades.findIndex((g) => g.name === plannedGrade)
                      : page.sortPicks.get(set.id)
                  }
                  onCompletedGradeChange={list.setCompletedGrade}
                  onFirstOwnersChanged={refresh}
                  clubImported={clubImported}
                />
                {/* After the first row, however many sets fit in it. */}
                {index ===
                  Math.min(
                    columns,
                    page.visible.length || page.notInPlan.length,
                  ) -
                    1 && (
                  <WingsCupBanner unit="gallery" className="col-span-full" />
                )}
              </Fragment>
            );
          })}
        </section>
      )}
      {sync.ui}
    </main>
  );
};

export default GalleryPage;
